import { useMemo, useState } from "react";
import { accountService } from "@miplatform/api";
import {
  CONTACT_CARD_EDIT_MODES,
  CONTACT_DISPLAY_TYPE,
  CONTACT_STATUSES,
  HTTP_STATUSES,
  MAX_PAGE_SIZE,
} from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { extractDigits } from "@miplatform/utils";

const INTERNAL_ACCOUNT_ID = "internal-account-id";

/**
 * Custom hook for managing the MiContactEdit view model.
 *
 * @param {Object} config - The config object for the MiContactEdit view model.
 * @param {string} config.currentEditMode - The current edit mode (e.g., `CONTACT_CARD_EDIT_MODES.CONTACT` or `CONTACT_CARD_EDIT_MODES.RELATIONSHIP`).
 * @param {string|Array} config.editMode - The edit mode(s) for the contact card. Possible values are defined in the `CONTACT_CARD_EDIT_MODES` constant.
 * @param {Object} config.referenceIds - Mapping of contact levels to their corresponding reference IDs.
 * @param {Function} config.onRefresh - Function to refresh data after creating, updating, or deleting relationships.
 * @param {Object} config.contact - The contact object containing contact information.
 * @param {Function} config.onClose - Function to close the edit mode.
 * @param {Object} config.edit - The edit object for managing contact details.
 * @param {Object} config.relationshipEdit - The edit object for managing relationship details.
 *
 * @returns {Object} Hook return values.
 * @returns {boolean} isLoading - Indicates if the operation is in progress.
 * @returns {boolean} isDeleteModalOpen - Indicates if the delete confirmation modal is open.
 * @returns {boolean} isRelationshipMode - Indicates if the component is in relationship edit mode.
 * @returns {boolean} isContactMode - Indicates if the component is in contact edit mode.
 * @returns {boolean} isCreateMode - Indicates if the component is in create mode (no existing contact).
 * @returns {Array} accounts - List of accounts for selection.
 * @returns {Array} contactLevelOptions - List of contact level options for selection.
 * @returns {Function} handleSubmit - Function to handle form submission.
 * @returns {Function} isFormValid - Function to validate the form.
 * @returns {Function} getAccounts - Function to fetch accounts.
 * @returns {Function} handleOpenDeleteModal - Function to open the delete confirmation modal.
 * @returns {Function} handleCloseDeleteModal - Function to close the delete confirmation modal.
 * @returns {Function} deleteRelationship - Function to delete a relationship.
 * @returns {Function} updateRelationship - Function to update a relationship.
 *
 * @example
 * const {
 *   isLoading,
 *   isDeleteModalOpen,
 *   isRelationshipMode,
 *   isContactMode,
 *   isCreateMode,
 *   accounts,
 *   contactLevelOptions,
 *   handleSubmit,
 *   isFormValid,
 *   getAccounts,
 *   handleOpenDeleteModal,
 *   handleCloseDeleteModal,
 *   deleteRelationship,
 *   updateRelationship,
 * } = useMiContactEditViewModel({
 *   currentEditMode: CONTACT_CARD_EDIT_MODES.CONTACT,
 *   editMode: CONTACT_CARD_EDIT_MODES.CONTACT,
 *   referenceIds: { COMMUNITIES: "ref-123" },
 *   onRefresh: () => console.log("Data refreshed"),
 *   contact: { accountId: "account-123", id: "contact-456" },
 *   onClose: () => console.log("Edit mode closed"),
 *   edit: contactEditObject,
 *   relationshipEdit: relationshipEditObject,
 * });
 */
/**
 * @param {string[] | undefined} accountIds - Optional list of account identifiers used to filter which accounts are available for selection.
 */
export const useMiContactEditViewModel = ({
  accountIds,
  accountTypes,
  currentEditMode,
  editMode,
  referenceIds,
  onRefresh,
  contact,
  onClose,
  edit,
  relationshipEdit,
}) => {
  const stateManager = useComponentState();
  const isLoading = stateManager.isBusy();
  const [accounts, setAccounts] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const contactLevelOptions = useMemo(
    () =>
      Object.keys(referenceIds || {}).map((key) => ({
        label: CONTACT_DISPLAY_TYPE[key],
        value: key,
      })),
    [referenceIds]
  );

  const { isRelationshipMode, isContactMode, isCreateMode } = useMemo(() => {
    if (currentEditMode === CONTACT_CARD_EDIT_MODES.RELATIONSHIP) {
      return { isRelationshipMode: true };
    }

    if (currentEditMode === CONTACT_CARD_EDIT_MODES.CONTACT) {
      return { isContactMode: true };
    }
    const isRelationshipMode = Array.isArray(editMode)
      ? editMode.includes(CONTACT_CARD_EDIT_MODES.RELATIONSHIP)
      : editMode === CONTACT_CARD_EDIT_MODES.RELATIONSHIP;

    const isContactMode = Array.isArray(editMode)
      ? editMode.includes(CONTACT_CARD_EDIT_MODES.CONTACT)
      : editMode === CONTACT_CARD_EDIT_MODES.CONTACT;

    return {
      isRelationshipMode,
      isContactMode,
      isCreateMode: !contact,
    };
  }, [currentEditMode, contact, editMode]);

  const handleOpenDeleteModal = () => setIsDeleteModalOpen(true);

  const handleCloseDeleteModal = () => setIsDeleteModalOpen(false);

  const getAccounts = () => {
    stateManager.run(async () => {
      const response = await accountService.accounts.getAll({
        sort: "name asc",
        top: MAX_PAGE_SIZE,
        ...((accountIds || accountTypes) && {
          filters: {
            ...(accountIds && { id: { in: accountIds } }),
            ...(accountTypes && { accountType: { in: accountTypes } }),
          },
        }),
      });
      if (response.status === HTTP_STATUSES.OK) {
        setAccounts([
          { label: "Internal", value: INTERNAL_ACCOUNT_ID },
          ...response.data.map((account) => ({
            ...account,
            label: account.name,
            value: account.id,
          })),
        ]);
      }
    });
  };

  const createContact = () =>
    stateManager.run(async () => {
      const contactResponse = await accountService.contacts.create({
        data: {
          ...edit.getValue(),
          accountId:
            edit.getValue("accountId") === INTERNAL_ACCOUNT_ID
              ? undefined
              : edit.getValue("accountId"),
          status: CONTACT_STATUSES.ACTIVE,
          phone: extractDigits(edit.getValue("phone")),
          ...(edit.getValue("secondaryPhone") && {
            secondaryPhone: extractDigits(edit.getValue("secondaryPhone")),
          }),
        },
        toastMessageConfig: {
          success: {
            text: "Contact created successfully",
          },
          failure: {
            text: "Failed to add new contact",
          },
        },
      });

      if (contactResponse.status === HTTP_STATUSES.CREATED) {
        if (isRelationshipMode) {
          const response =
            await accountService.contacts.createContactRelationship({
              id: contactResponse.data.id,
              data: {
                accountId: contactResponse.data.accountId,
                ...relationshipEdit.getValue(),
                RelatedReferenceId:
                  referenceIds[relationshipEdit.getValue("level")],
              },
            });

          if (response.status === HTTP_STATUSES.OK) {
            onRefresh(contactResponse.data);
          }
        } else {
          onRefresh(contactResponse.data);
        }
      }
    });

  const updateContact = () =>
    stateManager.run(async () => {
      const response = await accountService.contacts.update({
        id: contact.id,
        data: {
          ...edit.getValue(),
          accountId: contact.accountId,
          phone: extractDigits(edit.getValue("phone")),
          secondaryPhone: edit.getValue("secondaryPhone")
            ? extractDigits(edit.getValue("secondaryPhone"))
            : null,
        },
        toastMessageConfig: {
          success: {
            text: "Contact was updated successfully.",
          },
        },
      });

      if (response.status === HTTP_STATUSES.OK) {
        onClose();
        onRefresh();
      }
    });

  const deleteRelationship = async (isUpdate) => {
    handleCloseDeleteModal();
    stateManager.run(async () => {
      const response = await accountService.contacts.deleteContactRelationship({
        id: contact.id,
        data: {
          accountId: contact.accountId,
          label: contact.label,
          RelatedReferenceId: contact.relatedReferenceId,
        },
        toastMessageConfig: {
          success: {
            text: `Contact relationship ${
              isUpdate ? "updated" : "deleted"
            } successfully.`,
          },
          failure: {
            text: "Failed to delete contact relationship.",
          },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        onClose();
        onRefresh();
      }
    });
  };

  const updateRelationship = () => {
    stateManager.run(async () => {
      const relationship = relationshipEdit.getValue();
      const payload = {
        id: contact.id,
        data: {
          ...relationship,
          accountId: contact.accountId,
          RelatedReferenceId: referenceIds[relationship.level],
        },
      };

      if (relationship.label === contact.label) {
        const response =
          await accountService.contacts.updateContactRelationship({
            ...payload,
            toastMessageConfig: {
              success: {
                text: "Contact relationship updated successfully.",
              },
              failure: {
                text: "Failed to update contact relationship.",
              },
            },
          });

        if (response.status === HTTP_STATUSES.OK) {
          onClose();
          onRefresh();
        }
        return;
      }
      const response = await accountService.contacts.createContactRelationship(
        payload
      );

      if (response.status === HTTP_STATUSES.OK) {
        deleteRelationship(true);
      }
    });
  };

  const isFormValid = () => {
    if (isRelationshipMode && isContactMode) {
      return edit.isValid() && relationshipEdit.isValid();
    }

    if (isRelationshipMode) {
      return relationshipEdit.isValid() && relationshipEdit.isDirty();
    }

    if (isContactMode) {
      return edit.isValid() && edit.isDirty();
    }

    return false;
  };

  const handleSubmit = () => {
    if (isCreateMode) {
      createContact();
      return;
    }

    if (currentEditMode === CONTACT_CARD_EDIT_MODES.RELATIONSHIP) {
      updateRelationship();
      return;
    }
    if (currentEditMode === CONTACT_CARD_EDIT_MODES.CONTACT) {
      updateContact();
    }
  };

  return {
    isLoading,
    isDeleteModalOpen,
    isRelationshipMode,
    isContactMode,
    isCreateMode,
    accounts,
    contactLevelOptions,
    handleSubmit,
    isFormValid,
    getAccounts,
    handleOpenDeleteModal,
    handleCloseDeleteModal,
    deleteRelationship,
    updateRelationship,
  };
};
